// CWinCeremony — 牌局結束前的自摸／胡牌儀式
// 對照原版 MakeSelfAnimEntity、MakeSelfFanSprite、GameResultMarkSprite、
// BasePlayerEntity.winTileFlow/chuckerFlow 與 PressAnyKeyEntity。
//
// === 使用圖片 ===
// - public/assets/ui/round-self-draw.png（key: ROUND_SELF_DRAW_KEY）— 原版 270×198「自摸」靜止印記
// - public/assets/ui/round-self-draw-4x.png（key: ROUND_SELF_DRAW_4X_KEY）— 自摸放大動畫用 4× 圖
// - public/assets/ui/round-hu.png（key: ROUND_HU_KEY）— 原版 270×198「胡」靜止印記
// - public/assets/ui/round-hu-4x.png（key: ROUND_HU_4X_KEY）— 胡牌放大動畫用 4× 圖
// - public/assets/ui/round-chucker.png（key: ROUND_CHUCKER_KEY）— 原版 270×198「放槍」靜止印記
// - public/assets/ui/round-win-fan.png（key: ROUND_WIN_FAN_KEY）— 原版自摸扇光，Upscayl 4×
// - public/assets/ui/round-win-band.png（key: ROUND_WIN_BAND_KEY）— 原版紅黑漸層帶，Upscayl 4×
// - public/assets/ui/round-win-stars.png（key: ROUND_WIN_STARS_KEY）— 原版星光 spritesheet，Upscayl 4×
// - 放槍者門前的獨立胡張透過 CTileSprite 呈現，圖片清單見 src/ui/tile-sprite.ts 檔頭
// - 印記落定時的金星／金幣／閃光粒子透過 playCelebrationBurst 呈現，圖片清單見 src/ui/celebration-burst.ts 檔頭

import Phaser from 'phaser';

import {
  LOGICAL_HEIGHT,
  LOGICAL_WIDTH,
  SCREEN_CENTER_X,
  SCREEN_CENTER_Y,
} from '@config/game-config';
import type { TSeat } from '@core/types';
import {
  ROUND_CHUCKER_KEY,
  ROUND_HU_4X_KEY,
  ROUND_HU_KEY,
  ROUND_SELF_DRAW_4X_KEY,
  ROUND_SELF_DRAW_KEY,
  ROUND_WIN_BAND_KEY,
  ROUND_WIN_FAN_KEY,
  ROUND_WIN_STARS_KEY,
} from '@scenes/boot-scene';

import {
  baseMarkScaleOf,
  CWinCeremonyGate,
  selfDrawSeatMarkSeats,
  WinCeremonyKind,
  type buildWinCeremonyPlan,
} from './round-ceremony-timing';
import { BurstSize, playCelebrationBurst, shakeTable } from './celebration-burst';
import { SEAT_BADGE_SPOTS } from './seat-badges';
import { CTileSprite, TileFace } from './tile-sprite';
import { UIDepth } from './ui-depths';
import { chuckerTileOffsetOf } from './win-ceremony-layout';

// ---- 全畫面層與輸入 ----
const CEREMONY_DEPTH = UIDepth.WinCeremony; // 蓋過所有牌面、提示與橫幅
const INPUT_DEPTH = CEREMONY_DEPTH + 2; // 全畫面點擊攔截層
const CENTER_X = SCREEN_CENTER_X; // 儀式 container 中心 X
const CENTER_Y = SCREEN_CENTER_Y; // 儀式 container 中心 Y

// ---- 自摸橫幅／扇光（原版 MakeSelfAnimEntity / MakeSelfFanSprite）----
const SELF_DRAW_BAND_HEIGHT = 160; // 紅黑漸層帶顯示高度
const UPSCALED_ASSET_SCALE = 0.25; // Upscayl 4× 素材縮回原版畫面尺寸
const SELF_DRAW_MARK_SCALE = 0.28; // 中央「自摸」印記尺寸
const FAN_DISPLAY_WIDTH = 162; // 原版 win_hand_fan.png 顯示寬
const FAN_COUNT = Math.ceil(LOGICAL_WIDTH / FAN_DISPLAY_WIDTH) + 1; // 鋪滿全寬並多留一片
const FAN_PIVOT_Y = 0.78; // 扇柄旋轉中心（原版 rotationCenterY 約為圖高 78%）
const FAN_ROTATION_FROM = 180; // 原版扇光進場起始角度
const FAN_ROTATION_TO = 360; // 原版扇光進場結束角度
const FAN_FADE_MS = 700; // 原版 FAN_DURATION
const FAN_HOLD_MS = 1000; // 原版 FAN_STOP_DELAY
const FAN_STAGGER_MS = 100; // 原版 FAN_DELAY
const FAN_REPEAT_GAP_MS = 1000; // 原版循環尾端停頓
const SELF_DRAW_MARK_DELAY_MS = 700; // 第一批扇光展開後才落下自摸印記

// ---- 結果印記（原版 GameResultMarkSprite）----
const MARK_SCALE_FROM_RATE = 3; // 印記由 3× 縮回
const MARK_SCALE_MS = 700; // 原版 SCALE_DURATION
const MARK_LOD_BASE_FADE_DELAY_MS = 300; // 動畫後半先在 4× 圖下方淡入原始圖
const MARK_LOD_BASE_FADE_MS = 200; // 原始圖淡入完成後才開始移除 4× 圖，避免合成 alpha 降低
const MARK_LOD_HIGH_FADE_DELAY_MS = MARK_LOD_BASE_FADE_DELAY_MS + MARK_LOD_BASE_FADE_MS;
const MARK_LOD_HIGH_FADE_MS = MARK_SCALE_MS - MARK_LOD_HIGH_FADE_DELAY_MS;
const SELF_DRAW_SEAT_MARK_DELAY_MS = SELF_DRAW_MARK_DELAY_MS + MARK_SCALE_MS; // 中央印記縮回後才在贏家門前留章
const SEAT_MARK_SCALE = 0.18; // 胡／放槍印記顯示在各家門前的尺寸
const SHADOW_SCALE_TO_RATE = 1.5; // 胡牌印記影子向外擴大倍率
const SHADOW_ALPHA_FROM = 0.5; // 原版影子起始透明度
const SHADOW_PULSE_MS = 1000; // 原版影子放大淡出時長
const SHADOW_REPEAT_GAP_MS = 1000; // 原版影子每輪停頓
const CHUCKER_TILE_SCALE = 0.55; // 原版 FrontUpEnlargeTileSprite：放槍者門前的獨立大牌

// ---- 星環（原版 StarRingEntity）----
const STAR_COUNT = 30; // 原版每波 30 顆
const STAR_FRAME_COUNT = 12; // focus_star.png 3×4 grid
const STAR_RADIUS = 190; // 星光由印記中心射出的半徑
const STAR_SCALE_MIN = 0.1; // Upscayl 4× 星光顯示縮放下限
const STAR_SCALE_MAX = 0.18; // Upscayl 4× 星光顯示縮放上限
const STAR_BURST_MS = 1000; // 原版 EaseExponentialOut 飛散時長
const STAR_SECOND_WAVE_DELAY_MS = 1800; // 原版第二波延遲

// ---- 「點擊繼續」（原版 PressAnyKeyEntity）----
const SELF_DRAW_READY_MS = 2900; // 第一輪扇光、印記與兩波星光完成後才可繼續
const DISCARD_WIN_READY_MS = 2500; // 原版 mark 0.5~0.7s + PressAnyKey 2s
const PRESS_BAR_Y = LOGICAL_HEIGHT * (2 / 3); // 原版 camera height × 2/3
const PRESS_BAR_HEIGHT = 48; // 黑底提示條高度
const PRESS_BAR_ALPHA = 0.75; // 原版 PressAnyKeyEntity 黑底 alpha
const PRESS_TEXT = '點擊繼續'; // 原版 TEXT_PRESS_ANY_KEY 的觸控版文案
const PRESS_TEXT_SIZE_PX = 28; // 黃色提示字尺寸
const PRESS_TEXT_COLOR = '#fff900'; // 原版接近 RGB(254,250,3)
const PRESS_FADE_MS = 500; // 原版 FADEIN_DURATION
const PRESS_SHINE_MS = 1000; // 文字明暗閃耀單程
const PRESS_TEXT_DIM_ALPHA = 0.55; // 閃耀最低透明度

/** 只負責視覺與「動畫完成後才接受一次點擊」；牌局資料與 scene 切換由 caller 管理。 */
export class CWinCeremony {
  private readonly gate = new CWinCeremonyGate();
  private container: Phaser.GameObjects.Container | null = null;
  private inputZone: Phaser.GameObjects.Zone | null = null;
  private readonly timers: Phaser.Time.TimerEvent[] = [];

  constructor(private readonly scene: Phaser.Scene) {}

  play(
    plan: NonNullable<ReturnType<typeof buildWinCeremonyPlan>>,
    callbacks: {
      readonly playWinSound: (isSelfDraw: boolean) => void;
      readonly onRevealWinners: () => void;
      readonly onContinue: () => void;
    }
  ): void {
    this.destroy();
    this.gate.begin();

    const container = this.scene.add.container(CENTER_X, CENTER_Y).setDepth(CEREMONY_DEPTH);
    this.container = container;
    this.createInputGate(callbacks.onContinue);

    if (plan.kind === WinCeremonyKind.SelfDraw) {
      this.playSelfDraw(
        container,
        selfDrawSeatMarkSeats(plan),
        callbacks.playWinSound,
        callbacks.onRevealWinners
      );
      this.scheduleReady(SELF_DRAW_READY_MS);
      return;
    }

    this.playDiscardWin(
      container,
      plan.winnerSeats,
      plan.loserSeats,
      plan.discardedTiles,
      callbacks.playWinSound,
      callbacks.onRevealWinners
    );
    this.scheduleReady(DISCARD_WIN_READY_MS);
  }

  destroy(): void {
    for (const timer of this.timers) timer.remove(false);
    this.timers.length = 0;

    if (this.container) {
      this.scene.tweens.killTweensOf(this.container.list);
      this.container.destroy(true);
      this.container = null;
    }
    if (this.inputZone) {
      this.inputZone.removeAllListeners();
      this.inputZone.destroy();
      this.inputZone = null;
    }
  }

  private createInputGate(onContinue: () => void): void {
    const zone = this.scene.add
      .zone(CENTER_X, CENTER_Y, LOGICAL_WIDTH, LOGICAL_HEIGHT)
      .setDepth(INPUT_DEPTH)
      .setInteractive();
    zone.on('pointerdown', () => {
      if (!this.gate.requestContinue()) return;
      this.destroy();
      onContinue();
    });
    this.inputZone = zone;
  }

  private playSelfDraw(
    container: Phaser.GameObjects.Container,
    winnerSeats: readonly TSeat[],
    playWinSound: (isSelfDraw: boolean) => void,
    onRevealWinners: () => void
  ): void {
    const band = this.scene.add
      .image(0, 0, ROUND_WIN_BAND_KEY)
      .setDisplaySize(LOGICAL_WIDTH, SELF_DRAW_BAND_HEIGHT);
    container.add(band);

    const firstFanX = -LOGICAL_WIDTH / 2 + FAN_DISPLAY_WIDTH / 2;
    for (let i = 0; i < FAN_COUNT; i++) {
      const fan = this.scene.add
        .image(firstFanX + i * FAN_DISPLAY_WIDTH, 0, ROUND_WIN_FAN_KEY)
        .setScale(UPSCALED_ASSET_SCALE)
        .setOrigin(0.5, FAN_PIVOT_Y)
        .setAlpha(0)
        .setAngle(FAN_ROTATION_FROM)
        .setBlendMode(Phaser.BlendModes.ADD);
      container.add(fan);
      this.scene.tweens.add({
        targets: fan,
        angle: FAN_ROTATION_TO,
        alpha: 1,
        duration: FAN_FADE_MS,
        delay: i * FAN_STAGGER_MS,
        ease: 'Cubic.easeOut',
        hold: FAN_HOLD_MS,
        yoyo: true,
        repeat: -1,
        repeatDelay: FAN_REPEAT_GAP_MS,
      });
    }

    this.addTimer(SELF_DRAW_MARK_DELAY_MS, () => {
      onRevealWinners();
      const mark = this.createMark(container, 0, 0, ROUND_SELF_DRAW_KEY, SELF_DRAW_MARK_SCALE);
      playWinSound(true);
      this.animateMarkAndStars(container, mark, 0, 0, SELF_DRAW_MARK_SCALE, ROUND_SELF_DRAW_4X_KEY);
    });

    this.addTimer(SELF_DRAW_SEAT_MARK_DELAY_MS, () => {
      for (const seat of winnerSeats) {
        const spot = SEAT_BADGE_SPOTS[seat];
        const localX = spot.x - CENTER_X;
        const localY = spot.y - CENTER_Y;
        const mark = this.createMark(
          container,
          localX,
          localY,
          ROUND_SELF_DRAW_KEY,
          SEAT_MARK_SCALE
        );
        this.animateMarkWithShadow(
          container,
          mark,
          localX,
          localY,
          SEAT_MARK_SCALE,
          ROUND_SELF_DRAW_KEY,
          ROUND_SELF_DRAW_4X_KEY
        );
      }
    });
  }

  private playDiscardWin(
    container: Phaser.GameObjects.Container,
    winnerSeats: readonly TSeat[],
    loserSeats: readonly TSeat[],
    discardedTiles: NonNullable<ReturnType<typeof buildWinCeremonyPlan>>['discardedTiles'],
    playWinSound: (isSelfDraw: boolean) => void,
    onRevealWinners: () => void
  ): void {
    onRevealWinners();
    for (const seat of loserSeats) {
      const spot = SEAT_BADGE_SPOTS[seat];
      this.createMark(
        container,
        spot.x - CENTER_X,
        spot.y - CENTER_Y,
        ROUND_CHUCKER_KEY,
        SEAT_MARK_SCALE
      );
    }

    for (const discarded of discardedTiles) {
      const spot = SEAT_BADGE_SPOTS[discarded.fromSeat];
      const offset = chuckerTileOffsetOf(discarded.fromSeat);
      const tile = new CTileSprite(
        this.scene,
        spot.x - CENTER_X + offset.x,
        spot.y - CENTER_Y + offset.y,
        discarded.tile,
        TileFace.HandBottom
      ).setScale(CHUCKER_TILE_SCALE);
      container.add(tile);
    }

    for (const seat of winnerSeats) {
      const spot = SEAT_BADGE_SPOTS[seat];
      const localX = spot.x - CENTER_X;
      const localY = spot.y - CENTER_Y;
      const mark = this.createMark(container, localX, localY, ROUND_HU_KEY, SEAT_MARK_SCALE);
      this.animateMarkWithShadow(
        container,
        mark,
        localX,
        localY,
        SEAT_MARK_SCALE,
        ROUND_HU_KEY,
        ROUND_HU_4X_KEY
      );
    }
    this.addTimer(MARK_SCALE_MS, () => shakeTable(this.scene)); // 胡印記縮回落定瞬間輕震一次
    playWinSound(false);
  }

  private createMark(
    container: Phaser.GameObjects.Container,
    x: number,
    y: number,
    textureKey: string,
    targetScale: number
  ): Phaser.GameObjects.Image {
    const mark = this.scene.add.image(x, y, textureKey).setScale(baseMarkScaleOf(targetScale));
    container.add(mark);
    return mark;
  }

  private animateMarkAndStars(
    container: Phaser.GameObjects.Container,
    mark: Phaser.GameObjects.Image,
    centerX: number,
    centerY: number,
    targetScale: number,
    highResolutionTextureKey: string
  ): void {
    this.animateMarkLod(
      container,
      mark,
      centerX,
      centerY,
      targetScale,
      highResolutionTextureKey,
      () => {
        this.explodeStars(container, centerX, centerY);
        playCelebrationBurst(this.scene, CENTER_X + centerX, CENTER_Y + centerY, BurstSize.Large);
        shakeTable(this.scene);
      }
    );
  }

  private animateMarkWithShadow(
    container: Phaser.GameObjects.Container,
    mark: Phaser.GameObjects.Image,
    centerX: number,
    centerY: number,
    targetScale: number,
    baseTextureKey: string,
    highResolutionTextureKey: string
  ): void {
    this.animateMarkLod(
      container,
      mark,
      centerX,
      centerY,
      targetScale,
      highResolutionTextureKey,
      () => {
        const baseTargetScale = baseMarkScaleOf(targetScale);
        const shadow = this.scene.add
          .image(centerX, centerY, baseTextureKey)
          .setScale(baseTargetScale)
          .setAlpha(SHADOW_ALPHA_FROM);
        container.addAt(shadow, Math.max(0, container.getIndex(mark)));
        this.scene.tweens.add({
          targets: shadow,
          scaleX: baseTargetScale * SHADOW_SCALE_TO_RATE,
          scaleY: baseTargetScale * SHADOW_SCALE_TO_RATE,
          alpha: 0,
          duration: SHADOW_PULSE_MS,
          repeat: -1,
          repeatDelay: SHADOW_REPEAT_GAP_MS,
        });
        playCelebrationBurst(this.scene, CENTER_X + centerX, CENTER_Y + centerY, BurstSize.Medium);
      }
    );
  }

  /**
   * 放大階段用 4× 圖，動畫後半交叉淡入原始 270×198 圖。
   * 兩張圖同步縮放且畫面尺寸完全一致，避免切換瞬間跳動；靜止後只留下原始圖，
   * 避免非 POT 的 1080×792 貼圖被 WebGL 縮到約 18% 時產生 minification aliasing。
   */
  private animateMarkLod(
    container: Phaser.GameObjects.Container,
    baseMark: Phaser.GameObjects.Image,
    centerX: number,
    centerY: number,
    highResolutionTargetScale: number,
    highResolutionTextureKey: string,
    onComplete: () => void
  ): void {
    const baseTargetScale = baseMarkScaleOf(highResolutionTargetScale);
    const highResolutionMark = this.scene.add
      .image(centerX, centerY, highResolutionTextureKey)
      .setScale(highResolutionTargetScale * MARK_SCALE_FROM_RATE);
    container.add(highResolutionMark);

    baseMark.setScale(baseTargetScale * MARK_SCALE_FROM_RATE).setAlpha(0);

    this.scene.tweens.add({
      targets: baseMark,
      scaleX: baseTargetScale,
      scaleY: baseTargetScale,
      duration: MARK_SCALE_MS,
      ease: 'Cubic.easeIn',
    });
    this.scene.tweens.add({
      targets: highResolutionMark,
      scaleX: highResolutionTargetScale,
      scaleY: highResolutionTargetScale,
      duration: MARK_SCALE_MS,
      ease: 'Cubic.easeIn',
      onComplete: () => {
        highResolutionMark.destroy();
        baseMark.setAlpha(1).setScale(baseTargetScale);
        onComplete();
      },
    });
    this.scene.tweens.add({
      targets: baseMark,
      alpha: 1,
      delay: MARK_LOD_BASE_FADE_DELAY_MS,
      duration: MARK_LOD_BASE_FADE_MS,
      ease: 'Linear',
    });
    this.scene.tweens.add({
      targets: highResolutionMark,
      alpha: 0,
      delay: MARK_LOD_HIGH_FADE_DELAY_MS,
      duration: MARK_LOD_HIGH_FADE_MS,
      ease: 'Linear',
    });
  }

  private explodeStars(
    container: Phaser.GameObjects.Container,
    centerX: number,
    centerY: number
  ): void {
    for (let i = 0; i < STAR_COUNT; i++) {
      const angle = (i / STAR_COUNT) * Math.PI * 2;
      const targetScale = STAR_SCALE_MIN + Math.random() * (STAR_SCALE_MAX - STAR_SCALE_MIN);
      const star = this.scene.add
        .image(centerX, centerY, ROUND_WIN_STARS_KEY, i % STAR_FRAME_COUNT)
        .setScale(0)
        .setBlendMode(Phaser.BlendModes.ADD);
      container.add(star);
      this.scene.tweens.add({
        targets: star,
        x: centerX + Math.cos(angle) * STAR_RADIUS,
        y: centerY + Math.sin(angle) * STAR_RADIUS,
        scaleX: targetScale,
        scaleY: targetScale,
        alpha: 0,
        angle: Math.random() * 360,
        duration: STAR_BURST_MS,
        delay: i % 2 === 0 ? 0 : STAR_SECOND_WAVE_DELAY_MS,
        ease: 'Expo.easeOut',
      });
    }
  }

  private scheduleReady(delayMs: number): void {
    this.addTimer(delayMs, () => {
      this.gate.markAnimationComplete();
      this.showPressAnyKey();
    });
  }

  private showPressAnyKey(): void {
    const container = this.container;
    if (!container) return;

    const localY = PRESS_BAR_Y - CENTER_Y;
    const bar = this.scene.add
      .rectangle(0, localY, LOGICAL_WIDTH, PRESS_BAR_HEIGHT, 0x000000, PRESS_BAR_ALPHA)
      .setAlpha(0);
    const text = this.scene.add
      .text(0, localY, PRESS_TEXT, {
        fontFamily: 'sans-serif',
        fontSize: `${PRESS_TEXT_SIZE_PX}px`,
        color: PRESS_TEXT_COLOR,
      })
      .setOrigin(0.5)
      .setAlpha(0);
    container.add([bar, text]);

    this.scene.tweens.add({
      targets: [bar, text],
      alpha: 1,
      duration: PRESS_FADE_MS,
      ease: 'Linear',
      onComplete: () => {
        this.scene.tweens.add({
          targets: text,
          alpha: PRESS_TEXT_DIM_ALPHA,
          duration: PRESS_SHINE_MS,
          ease: 'Sine.easeInOut',
          yoyo: true,
          repeat: -1,
        });
      },
    });
  }

  private addTimer(delayMs: number, callback: () => void): void {
    this.timers.push(this.scene.time.delayedCall(delayMs, callback));
  }
}
